import { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { tokens, Text, Icon } from '@hotjar/hotsauce';

import { SiteIcon } from '@hotjar/webapp/features/Activation/SiteIcon';
import { useCurrentSite } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { Card } from '@hotjar/webapp/components/Card';
import SuccessfulInstallationIllustration from '@hotjar/shared-assets/images/successful-installation-new.jpg';
import { disablePostInstallCard } from './usePostInstallView';
import { PlanDetails } from './PlanDetails';

const CARD_ELEMENT_NAME = 'Successful Installation Card';
const CLOSE_ELEMENT_NAME = `Successful Installation Card Close`;
const UI_POSITION = 'Dashboard';

const SiteUrlCaption = styled(Text)`
  display: flex;
  align-items: center;
`;

const Favicon = styled(SiteIcon)`
  margin-left: -${tokens.spacer.x2};
  margin-right: ${tokens.spacer.x1};
`;

const CheckIcon = styled(Icon)`
  position: relative;
  z-index: 1;
  background: ${tokens.color.ui_background};
  border-radius: 100%;
  margin-right: ${tokens.spacer.x1};
`;

const Header = styled(Text)`
  margin: ${tokens.spacer.x3} 0 ${tokens.spacer.x4};
`;

const Illustration = styled.img`
  position: absolute;
  height: 100%;
  right: ${tokens.spacer.x2};
  top: 0;
  width: auto;
`;

const Content = styled.div`
  padding: ${tokens.spacer.x2};
  overflow: hidden;
`;

const StyledCard = styled(Card)`
  border: 1px solid ${tokens.color.ui_border_active};
  border-radius ${tokens.radius.m};
  margin-bottom: ${tokens.spacer.x4};
`;

export function PostInstallCard(): JSX.Element | null {
  const { data: currentSite } = useCurrentSite();
  const url = currentSite?.url;
  const [isVisible, setIsVisible] = useState(true);
  const { t } = useTranslation();
  const track = useTrack();
  const trackingData = useMemo(
    () => ({
      account_id: currentSite?.account?.id,
      organization_id: currentSite?.organization?.id,
      site_id: currentSite?.id,
    }),
    [currentSite],
  );

  const handleDismiss = () => {
    setIsVisible(false);
    if (currentSite) {
      track.click(CLOSE_ELEMENT_NAME, UI_POSITION, trackingData);
      disablePostInstallCard(currentSite?.id);
    }
  };

  useEffect(() => {
    track.viewElement(CARD_ELEMENT_NAME, UI_POSITION, trackingData);
  }, [track, trackingData]);

  if (!isVisible) return null;

  return (
    <StyledCard onDismiss={handleDismiss}>
      <Card.Body>
        <Content>
          <SiteUrlCaption
            textType="caption"
            textColor="text_light"
            as="span"
            data-testid="post-install-card-status"
          >
            <CheckIcon icon="check_alt" size="m" color="text_success" />
            {url ? <Favicon siteUrl={url} /> : null}
            {url
              ? t('activation:dashboard.post-install-card.subtitle-with-site', { url })
              : t('activation:dashboard.post-install-card.subtitle-without-site')}
          </SiteUrlCaption>
          <Header textType="header4" as="h4" data-testid="post-install-card-header">
            {t('activation:dashboard.post-install-card.title')}
          </Header>
          <Illustration src={SuccessfulInstallationIllustration} width="408" height="202" />
          {currentSite ? <PlanDetails site={currentSite} /> : null}
        </Content>
      </Card.Body>
    </StyledCard>
  );
}
